/**
 * RAHistoryViewer - Audit Trail Viewer for Risk Assessments
 *
 * Displays the change history for risk assessments including
 * creates, updates, deletions, and signature additions.
 *
 * @module components/RAHistoryViewer
 */

import React, { useState, useMemo } from 'react';
import {
  Download,
  Search,
  History
} from 'lucide-react';
import { useAuditTrail } from '../../../hooks/useAuditTrail';
import BackButton from '../../../components/tailwind/common/BackButton';
import Dropdown from '../../../components/common/Dropdown';
import { toLocalYMD } from '../../../utils/dateFmt';

/**
 * RAHistoryViewer Component
 * @param {Object} props - Component props
 * @param {Function} props.onBack - Handler for back navigation
 * @param {string} props.assessmentId - Optional: filter to specific assessment
 */
const RAHistoryViewer = ({ onBack, assessmentId = null, assessments = [] }) => {
  // FT-515: getChanges now reads from async-hydrated storage state; isLoading
  // is exposed so this viewer can show a loading state instead of flashing
  // "No audit entries found" before the storage read resolves.
  const { getChanges, isLoading } = useAuditTrail('risk-assessment');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterAssessmentId, setFilterAssessmentId] = useState(assessmentId || 'all');

  // Get audit entries (optionally filtered to specific assessment)
  const allEntries = useMemo(() => {
    const entries = getChanges();
    const targetId = filterAssessmentId !== 'all' ? filterAssessmentId : assessmentId;
    if (targetId) {
      return entries.filter(e => e.item?.id === targetId);
    }
    return entries;
  }, [getChanges, assessmentId, filterAssessmentId]);

  // Apply filters
  const filteredEntries = useMemo(() => {
    let entries = [...allEntries];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      entries = entries.filter(entry =>
        entry.item?.name?.toLowerCase().includes(lower) ||
        entry.user?.name?.toLowerCase().includes(lower) ||
        entry.reason?.label?.toLowerCase().includes(lower)
      );
    }

    if (filterAction !== 'all') {
      entries = entries.filter(entry => entry.action === filterAction);
    }

    // Date range filter
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      entries = entries.filter(entry => new Date(entry.timestamp) >= start);
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      entries = entries.filter(entry => new Date(entry.timestamp) <= end);
    }

    // Sort by date descending
    return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [allEntries, searchTerm, filterAction, startDate, endDate]);

  // Export to CSV
  const handleExport = () => {
    const headers = ['Date/Time', 'Action', 'Assessment', 'User', 'Hazard Count', 'Risk Level', 'Status', 'Changes', 'Reason'];
    const rows = filteredEntries.map(entry => {
      // Determine changes description
      let changesDesc = entry.changes?.field || '';
      if (entry.action === 'update' && entry.changes?.before && entry.changes?.after) {
        const changedFields = Object.keys(entry.changes.after).filter(key =>
          JSON.stringify(entry.changes.before[key]) !== JSON.stringify(entry.changes.after[key])
        );
        // Show actual values in CSV
        changesDesc = changedFields.map(key => {
          const before = entry.changes.before[key] ?? '(none)';
          const after = entry.changes.after[key] ?? '(none)';
          return `${key}: ${before} → ${after}`;
        }).join('; ') || 'Assessment updated';
      }

      return [
        entry.timestampLocal || '',
        entry.action || '',
        entry.item?.name || '',
        entry.user?.name || '',
        entry.item?.hazardCount ?? '',
        entry.item?.riskLevel || '',
        entry.item?.status || '',
        changesDesc,
        entry.reason?.label || ''
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ra-audit-log-${toLocalYMD(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getActionLabel = (entry) => {
    if (entry.action === 'update' && entry.changes?.field === 'signature') {
      return `Signature added (${entry.changes.type})`;
    }
    switch (entry.action) {
      case 'create':
        return 'Created';
      case 'update':
        return 'Updated';
      case 'delete':
        return entry.deleteType === 'hard' ? 'Permanently Deleted' : 'Deleted';
      case 'restore':
        return 'Restored';
      default:
        return entry.action.charAt(0).toUpperCase() + entry.action.slice(1);
    }
  };

  // FT-515: Badge colour aligned to ChangeLogViewer standard (icon tile removed — badge is the reference)
  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create':
        return 'bg-blue-100 text-blue-800';
      case 'update':
        return 'bg-gray-100 text-gray-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      case 'restore':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Filter Section — 3-row template matching ChangeLogViewer */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="space-y-3">
          {/* Row 1: Title + Export + Back */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BackButton onClick={onBack} />
              <h3 className="text-lg font-medium text-gray-900">
                {assessmentId ? 'Assessment History' : 'Risk Assessment Audit Log'}
              </h3>
            </div>
            <button
              onClick={handleExport}
              disabled={filteredEntries.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
          </div>

          {/* Row 2: Search + Action filter + Assessment filter + Date range */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by assessment name, user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Dropdown
                value={filterAction}
                onChange={setFilterAction}
                width="w-40"
                options={[
                  { value: 'all', label: 'All Actions' },
                  { value: 'create', label: 'Created' },
                  { value: 'update', label: 'Updated' },
                  { value: 'delete', label: 'Deleted' },
                  { value: 'restore', label: 'Restored' }
                ]}
              />
              {assessments.length > 0 && !assessmentId && (
                <Dropdown
                  value={filterAssessmentId}
                  onChange={setFilterAssessmentId}
                  width="w-56"
                  options={[
                    { value: 'all', label: 'All Assessments' },
                    ...assessments.map(a => ({
                      value: a.id,
                      label: a.title?.substring(0, 30) + (a.title?.length > 30 ? '...' : '') || 'Untitled'
                    }))
                  ]}
                />
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">From:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
                />
                <span className="text-sm text-gray-500">To:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Entry count */}
          <div>
            <span className="text-sm text-gray-500">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
              {searchTerm || filterAction !== 'all' || startDate || endDate ? ' (filtered)' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-24 md:pb-6">
        {/* Summary Stats — above the table */}
        {allEntries.length > 0 && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {allEntries.filter(e => e.action === 'create').length}
              </div>
              <div className="text-sm text-gray-500">Created</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-600">
                {allEntries.filter(e => e.action === 'update').length}
              </div>
              <div className="text-sm text-gray-500">Updated</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-red-600">
                {allEntries.filter(e => e.action === 'delete').length}
              </div>
              <div className="text-sm text-gray-500">Deleted</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {allEntries.filter(e => e.changes?.field === 'signature').length}
              </div>
              <div className="text-sm text-gray-500">Signatures</div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="p-12 text-center">
            <History className="h-12 w-12 mx-auto mb-4 text-gray-300 animate-pulse" />
            <p className="text-gray-600 font-medium">Loading audit log…</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="p-12 text-center">
            <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 font-medium">No audit entries found</p>
            <p className="text-sm text-gray-500 mt-1">
              {allEntries.length === 0
                ? 'Changes to risk assessments will appear here'
                : 'Try adjusting your search or filters'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Assessment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Timestamp</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getActionBadgeColor(entry.action)}`}>
                        {getActionLabel(entry)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {entry.item?.name || 'Unknown Assessment'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {entry.user?.name || 'Unknown User'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {entry.timestampLocal || 'Unknown time'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-50 shadow-lg">
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={filteredEntries.length === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
          <button
            onClick={onBack}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RAHistoryViewer;
